// src/pages/EventoVendasPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../services/api";
import Swal from "sweetalert2";

interface ItemVenda {
    id: number;
    produtoId: number;
    produtoNome: string;
    quantidade: number;
    precoUnitario: number;
    precoComDesconto: number;
    subtotal: number;
}

interface Venda {
    id: number;
    dataHora: string;
    valorTotal: number;
    statusId: number;
    itens?: ItemVenda[];
}

const EventoVendasPage: React.FC = () => {
    const { id: eventoId } = useParams<{ id: string }>();

    const [vendas, setVendas] = useState<Venda[]>([]);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [eventoDescricao, setEventoDescricao] = useState("");

    const formatDateTime = (date?: string | null) => {
        if (!date) return "—";

        const d = new Date(date);
        return d.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const carregarVendas = useCallback(async () => {
        try {
            setLoading(true);

            const resVendas = await api.get(`/Vendas/evento/${eventoId}`);
            const resEvento = await api.get(`/evento/${eventoId}`);

            setVendas(resVendas.data || []);
            setEventoDescricao(resEvento.data?.descricao ?? "");
        } catch {
            Swal.fire("Erro", "Não foi possível carregar as vendas.", "error");
        } finally {
            setLoading(false);
        }
    }, [eventoId]);

    useEffect(() => {
        carregarVendas();
    }, [carregarVendas]);

    const confirmarEstorno = (vendaId: number) => {
        Swal.fire({
            title: "Estornar venda?",
            text: "Essa ação irá cancelar a venda e estornar o estoque.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sim, estornar!",
            cancelButtonText: "Cancelar",
        }).then((res) => {
            if (res.isConfirmed) estornarVenda(vendaId);
        });
    };

    const estornarVenda = async (vendaId: number) => {
        try {
            await api.post(`/Vendas/${vendaId}/cancelar`);

            Swal.fire({
                icon: "success",
                title: "Venda estornada!",
                timer: 1600,
                showConfirmButton: false,
            });

            carregarVendas();
        } catch (error: any) {
            Swal.fire(
                "Erro",
                error?.response?.data?.message || "Falha ao estornar venda.",
                "error"
            );
        }
    };

    const toggleExpand = (id: number) => {
        setExpanded((prev) => (prev === id ? null : id));
    };

    const getStatusBadge = (statusId: number) => {
        if (statusId === 1)
            return <span className="badge bg-success px-3 py-2">Concluída</span>;

        if (statusId === 2)
            return <span className="badge bg-warning text-dark px-3 py-2">Pendente</span>;

        return <span className="badge bg-danger px-3 py-2">Cancelada</span>;
    };

return (
  <div className="container-fluid">
    {/* CABEÇALHO */}
    <div className="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 className="mb-1">🛒 Vendas do Evento</h2>
        <p className="text-muted mb-0">
          <strong>{eventoDescricao}</strong>
        </p>
      </div>

<button
  className="btn btn-outline-secondary btn-sm"
  onClick={() => window.location.href = "/eventos"}
>
  ⬅ Voltar para Eventos
</button>
    </div>

    {loading && <p>Carregando vendas...</p>}

    {!loading && vendas.length === 0 && (
      <div className="alert alert-warning">
        Nenhuma venda registrada para este evento.
      </div>
    )}

    {!loading &&
      vendas.map((venda) => {
        const isOpen = expanded === venda.id;
        const cancelada = venda.statusId === 3;

        return (
          <div key={venda.id} className="card shadow-sm mb-3">
            {/* HEADER DA VENDA */}
            <div
              className={`card-header d-flex justify-content-between align-items-center ${
                cancelada ? "bg-light" : ""
              }`}
            >
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <strong>Venda #{venda.id}</strong>

                {getStatusBadge(venda.statusId)}

                <span className="text-muted">
                  {formatDateTime(venda.dataHora)}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <strong className="fs-6">
                  {venda.valorTotal.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </strong>

                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => toggleExpand(venda.id)}
                >
                  {isOpen ? "Fechar" : "Detalhes"}
                </button>

                {venda.statusId === 1 && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => confirmarEstorno(venda.id)}
                  >
                    Estornar
                  </button>
                )}

                {venda.statusId === 3 && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled
                  >
                    Estornada
                  </button>
                )}
              </div>
            </div>

            {/* ITENS DA VENDA */}
            {isOpen && venda.itens && (
              <div className="card-body bg-light">
                <h6 className="fw-bold mb-3 text-secondary">
                  Itens da venda
                </h6>

                {venda.itens.map((item) => (
                  <div
                    key={item.id}
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                  >
                    <div>
                      {item.produtoNome}{" "}
                      <small className="text-muted">
                        (x{item.quantidade})
                      </small>
                    </div>

                    <strong>
                      {item.subtotal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
  </div>
);


};

export default EventoVendasPage;
